import {
  AfterViewInit,
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  ViewChild,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import Quill from "quill";

@Component({
  selector: "app-rich-text-editor",
  standalone: true,
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
  templateUrl: "./RichTextEditorComponent.html",
  styleUrl: "./RichTextEditorComponent.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RichTextEditorComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild("editorContainer", { static: true }) editorContainer!: ElementRef;

  @Input() placeholder = "Enter description...";
  @Input() disabled = false;
  @Output() contentChange = new EventEmitter<string>();

  private quill: Quill | null = null;
  private onChange: (value: string) => void = () => {
    // Changed from empty arrow function
  };
  private onTouched: () => void = () => {
    // Changed from empty arrow function
  };
  private _content = "";

  get content(): string {
    return this._content;
  }

  set content(value: string) {
    this._content = value;
    this.onChange(value);
    this.onTouched();
    this.contentChange.emit(value);
  }

  writeValue(value: string): void {
    this._content = value || "";
    if (this.quill) {
      // Set content without triggering text-change event
      this.quill.root.innerHTML = this._content;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.quill) {
      this.quill.enable(!this.disabled);
    }
  }

  ngAfterViewInit(): void {
    if (this.editorContainer) {
      this.initQuill();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["disabled"]) {
      this.quill?.enable(!this.disabled);
    }
  }

  ngOnDestroy(): void {
    this.quill = null;
  }

  private initQuill(): void {
    this.quill = new Quill(this.editorContainer.nativeElement, {
      theme: "snow",
      placeholder: this.placeholder,
      readOnly: this.disabled,
      modules: {
        toolbar: {
          container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "image"],
            ["clean"],
          ],
          handlers: {
            image: this.imageHandler.bind(this),
          },
        },
      },
    });

    // Set initial content
    if (this._content) {
      this.quill.root.innerHTML = this._content;
    }

    // Listen for text changes
    this.quill.on("text-change", () => {
      const html = this.quill?.root.innerHTML || "";
      this.content = html;
    });

    // Listen for blur to mark as touched
    this.quill.on("selection-change", (range) => {
      if (!range) {
        this.onTouched();
      }
    });
  }

  private imageHandler(): void {
    // Create file input element
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    // Listen for file selection
    input.onchange = () => {
      const files = input.files;
      if (!files || files.length === 0) {
        return;
      }

      const file = files[0];

      // Read file as Data URL (base64) and insert into editor
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target?.result as string;
        const range = this.quill?.getSelection(true);

        if (range && this.quill) {
          this.quill.insertEmbed(range.index, "image", base64Image);
          this.quill.setSelection(range.index + 1);
        }
      };
      reader.readAsDataURL(file);
    };
  }
}
